import moment from "moment";
import pluralize from "pluralize";
import { matchPath } from "react-router-dom";

import { toast } from "@skydio/rivet-ui";

import type { Vehicle } from "hooks/useLivestreamingVehicles";
import type { SharedLink } from "hooks/useSharedLinks";

export const getSharedLinkUrl = (token: string) => {
  return `${window.location.origin}/shared_link/${token}`;
};

// Given viewport width, and height, calculate the video
// player dimensions for livestreams.
export const VIDEO_ASPECT_RATIO = 16 / 9;
export const getStreamingViewMediaDimensions = (width: number, height: number) =>
  width / height > VIDEO_ASPECT_RATIO
    ? { height, width: height * VIDEO_ASPECT_RATIO }
    : { width, height: width / VIDEO_ASPECT_RATIO };

/* Copy Link button press handler */
export const onCopyLinkButtonClicked = async (token: string) => {
  await navigator.clipboard.writeText(getSharedLinkUrl(token));
  toast.success("Link Copied", { duration: 3000 });
};

export type ReadyLinkType = "live" | "waiting" | "scheduled" | "expired";

export const readyLinkExpired = (sl: SharedLink) => {
  return moment(sl?.expiresAt).isBefore(moment());
};

export const readyLinkScheduled = (sl: SharedLink) => {
  return !readyLinkExpired(sl) && moment(sl?.startsAt).isAfter(moment());
};

// NOTE(Tommy): with the introduction of multi-vehicle ReadyLinks, a ReadyLink is
// considered "LIVE" if any vehicle it is linked to via its shared resources is live.
export const readyLinkLive = (sl: SharedLink, vehicles: Vehicle[]) => {
  const sharedResources = sl.sharedResources?.edges.map(sr => sr?.node);
  return sharedResources?.find(
    sr =>
      !!vehicles.find(v => v.vehicleId === sr?.vehicle?.vehicleId) ||
      (sr?.resourceGroup && !!vehicles.length)
  );
};

export const readyLinkClassification: (sl: SharedLink, lv: Vehicle[]) => ReadyLinkType = (
  sr,
  lvs
) => {
  if (readyLinkExpired(sr)) return "expired";
  else if (readyLinkScheduled(sr)) return "scheduled";
  else if (readyLinkLive(sr, lvs)) return "live";
  return "waiting";
};

export const readyLinkName = (sl: SharedLink | null) => {
  const sharedResources = sl?.sharedResources?.edges.map(sr => sr?.node) ?? [];
  const [firstSharedResource] = sharedResources;
  return (
    /* All ReadyLinks should have a name - return that first. */
    sl?.name ??
    /* If for some reason we need a fallback, follow pattern SkydioX10-abcd (and N others) Live Stream */
    (firstSharedResource?.vehicle?.displayName
      ? `${firstSharedResource?.vehicle?.displayName}${sharedResources.length > 1 ? ` (and ${pluralize("other", sharedResources.length - 1, true)})` : ""} Live Stream`
      : /* Should never encounter this case, but use "ReadyLink" as last resort. */
        "ReadyLink")
  );
};

export const matchSharedLinkPath = () => {
  return matchPath(window.location.pathname, { path: "/shared_link/:token" });
};

export const readyLinkIsAutomated = (sl: SharedLink | null) => {
  return sl?.automated ?? false;
};
